import { useEffect, useMemo, useState } from 'react'
import { getConteudoChave, putConteudoChave } from '@/api/adminApi'
import {
  CUSTOM_STYLE_ID,
  DEFAULT_CATALOG_KEY,
  WHITE_LABEL_THEME_CATALOG,
  catalogKey,
  findCatalogStyle,
  findCatalogStyleByKey,
  isCustomCatalogKey,
  matchCatalogKeyFromColors,
  parseCatalogKey,
  type ThemePaletteColors,
} from '@/data/whiteLabelThemeCatalog'
import { ADMIN_KEYS, CONTEUDO_API_KEYS } from '@/utils/adminStorage'
import {
  applyTheme,
  DEFAULT_THEME,
  loadAndApplyTheme,
  parseTheme,
  saveThemeToCache,
  serializeTheme,
  themeToScopeStyle,
  type AppThemeConfig,
} from '@/utils/theme'
import { buildThemeFromPalette } from '@/utils/themePalette'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import adminStyles from './Admin.module.css'
import styles from './AdminTemaPage.module.css'

const PALETTE_SLOTS: Array<{ key: keyof ThemePaletteColors; pct: string; title: string }> = [
  { key: 'primaria', pct: '60%', title: 'Primária (60%) — fundo e tom da marca' },
  { key: 'secundaria', pct: '30%', title: 'Secundária (30%) — cartões e superfícies' },
  { key: 'destaque', pct: '10%', title: 'Destaque (10%) — botões e acções' },
]

function normalizeHex(value: string, fallback: string): string {
  let v = value.trim()
  if (!v.startsWith('#')) v = `#${v}`
  if (/^#[0-9A-Fa-f]{6}$/.test(v)) return v.toUpperCase()
  return fallback
}

function paletteFromStyle(style: { primaria: string; secundaria: string; destaque: string }): ThemePaletteColors {
  return {
    primaria: style.primaria,
    secundaria: style.secundaria,
    destaque: style.destaque,
  }
}

function resolveSelection(
  theme: AppThemeConfig
): { nicheId: string; styleId: string; customPalette?: ThemePaletteColors } {
  if (theme.catalogKey) {
    const parsed = parseCatalogKey(theme.catalogKey)
    if (parsed) {
      if (parsed.styleId === CUSTOM_STYLE_ID) {
        return { ...parsed, customPalette: theme.customPalette }
      }
      if (findCatalogStyle(parsed.nicheId, parsed.styleId)) return parsed
    }
  }
  const matched = matchCatalogKeyFromColors(theme.bg, theme.bgCard, theme.accent)
  if (matched) {
    const parsed = parseCatalogKey(matched)
    if (parsed) return parsed
  }
  const fallback = parseCatalogKey(DEFAULT_CATALOG_KEY)
  return fallback ?? { nicheId: 'tecnologia', styleId: 'clean-sw' }
}

export function AdminTemaPage() {
  const [theme, setTheme] = useState<AppThemeConfig>(DEFAULT_THEME)
  const [nicheId, setNicheId] = useState('tecnologia')
  const [styleId, setStyleId] = useState('clean-sw')
  const [customColors, setCustomColors] = useState<ThemePaletteColors>({
    primaria: '#2563EB',
    secundaria: '#F1F5F9',
    destaque: '#0EA5E9',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  const apiChave = CONTEUDO_API_KEYS[ADMIN_KEYS.tema]

  const activeNiche = useMemo(
    () => WHITE_LABEL_THEME_CATALOG.find((n) => n.id === nicheId) ?? WHITE_LABEL_THEME_CATALOG[0],
    [nicheId]
  )

  const isCustom = styleId === CUSTOM_STYLE_ID

  const activeStyle = useMemo(() => {
    if (isCustom) {
      return {
        id: CUSTOM_STYLE_ID,
        nome: 'Personalizado',
        vibe: 'Escolha as três cores da sua marca.',
        ...customColors,
      }
    }
    return activeNiche.estilos.find((s) => s.id === styleId) ?? activeNiche.estilos[0]
  }, [activeNiche, styleId, isCustom, customColors])

  const previewTheme = useMemo(
    () => buildThemeFromPalette(activeStyle, catalogKey(activeNiche.id, activeStyle.id)),
    [activeNiche.id, activeStyle]
  )

  const previewKey = isCustom
    ? `${nicheId}:${styleId}:${customColors.primaria}:${customColors.secundaria}:${customColors.destaque}`
    : `${nicheId}:${styleId}`

  useEffect(() => {
    if (isCustom) return
    const valid = activeNiche.estilos.some((s) => s.id === styleId)
    if (!valid) setStyleId(activeNiche.estilos[0].id)
  }, [activeNiche, styleId, isCustom])

  useEffect(() => {
    if (loading) return
    applyTheme(previewTheme)
  }, [previewTheme, loading])

  useEffect(() => {
    return () => {
      void loadAndApplyTheme()
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setError(null)
    getConteudoChave(apiChave)
      .then((valor) => {
        if (cancelled) return
        const parsed = parseTheme(valor)
        const selection = resolveSelection(parsed)
        setTheme(parsed)
        setNicheId(selection.nicheId)
        setStyleId(selection.styleId)
        if (selection.customPalette) {
          setCustomColors(selection.customPalette)
        } else if (selection.styleId === CUSTOM_STYLE_ID) {
          const niche = WHITE_LABEL_THEME_CATALOG.find((n) => n.id === selection.nicheId)
          if (niche?.estilos[0]) setCustomColors(paletteFromStyle(niche.estilos[0]))
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar tema')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [apiChave])

  const canSave = useMemo(() => !loading && !saving, [loading, saving])

  const persistTheme = async (nextTheme: AppThemeConfig, successMessage = 'Tema guardado com sucesso.') => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await putConteudoChave(apiChave, serializeTheme(nextTheme))
      setTheme(nextTheme)
      applyTheme(nextTheme)
      saveThemeToCache(nextTheme)
      setSuccess(successMessage)
      notifySaved(successMessage)
    } catch (e) {
      notifyError(e, 'Erro ao guardar tema')
      setError(e instanceof Error ? e.message : 'Erro ao guardar tema')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectStyle = (nextNicheId: string, nextStyleId: string) => {
    setNicheId(nextNicheId)
    setStyleId(nextStyleId)
  }

  const handleSelectCustom = () => {
    if (!isCustom) {
      const base = activeNiche.estilos.find((s) => s.id === styleId) ?? activeNiche.estilos[0]
      if (base) setCustomColors(paletteFromStyle(base))
    }
    handleSelectStyle(activeNiche.id, CUSTOM_STYLE_ID)
  }

  const handleCustomColorChange = (key: keyof ThemePaletteColors, raw: string) => {
    setCustomColors((prev) => ({
      ...prev,
      [key]: normalizeHex(raw, prev[key]),
    }))
    if (!isCustom) handleSelectStyle(activeNiche.id, CUSTOM_STYLE_ID)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const key = catalogKey(activeNiche.id, activeStyle.id)
    const built = buildThemeFromPalette(activeStyle, key)
    const next: AppThemeConfig = isCustom ? { ...built, customPalette: customColors } : built
    await persistTheme(next)
  }

  const handleReset = () => {
    const defaultEntry = findCatalogStyleByKey(DEFAULT_CATALOG_KEY)
    if (!defaultEntry) return
    setNicheId(defaultEntry.niche.id)
    setStyleId(defaultEntry.style.id)
    setTheme(buildThemeFromPalette(defaultEntry.style, DEFAULT_CATALOG_KEY))
  }

  if (loading) return <p className={adminStyles.placeholderMsg}>A carregar…</p>

  const savedEntry = findCatalogStyleByKey(theme.catalogKey)
  const savedCustom = isCustomCatalogKey(theme.catalogKey)
  const savedNicheName = parseCatalogKey(theme.catalogKey)?.nicheId
    ? WHITE_LABEL_THEME_CATALOG.find((n) => n.id === parseCatalogKey(theme.catalogKey)?.nicheId)?.nome
    : null

  return (
    <div className={styles.page}>
      <header className={adminStyles.sectionHeader}>
        <h1 className={adminStyles.sectionTitle}>Tema e layout</h1>
        <p className={adminStyles.sectionSubtitle}>
          Escolha o nicho e o estilo da sua marca. O tema aplica-se ao painel admin, à app do cliente e a
          todas as páginas da empresa — guarde para confirmar.
        </p>
      </header>

      {error && (
        <div className={adminStyles.erroSection}>
          <p className={adminStyles.erro}>{error}</p>
        </div>
      )}
      {success && (
        <div className={adminStyles.successSection}>
          <p className={adminStyles.successMsg}>{success}</p>
        </div>
      )}

      <div className={styles.ruleCard}>
        <div className={styles.ruleItem}>
          <span className={styles.rulePct}>Fundo</span>
          <span className={styles.ruleLabel}>Tom da marca visível, sem poluir a leitura</span>
        </div>
        <div className={styles.ruleItem}>
          <span className={styles.rulePct}>Cartões</span>
          <span className={styles.ruleLabel}>Branco ou superfície suave — leitura confortável</span>
        </div>
        <div className={styles.ruleItem}>
          <span className={styles.rulePct}>Destaque</span>
          <span className={styles.ruleLabel}>Botões e acções com contraste legível</span>
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.mainCol}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Nicho de mercado</h2>
            <p className={styles.cardHint}>15 categorias curadas para 2025/2026.</p>
            <div className={styles.nicheGrid}>
              {WHITE_LABEL_THEME_CATALOG.map((niche) => (
                <button
                  key={niche.id}
                  type="button"
                  className={`${styles.nicheBtn} ${nicheId === niche.id ? styles.nicheBtnActive : ''}`}
                  onClick={() => {
                    const first = niche.estilos[0]
                    handleSelectStyle(niche.id, first.id)
                  }}
                >
                  {niche.nome}
                </button>
              ))}
            </div>
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Estilo — {activeNiche.nome}</h2>
            <p className={styles.cardHint}>Três paletas por nicho ou crie a sua com as cores da marca.</p>
            <div className={styles.styleGrid}>
              {activeNiche.estilos.map((style) => {
                const selected = styleId === style.id
                return (
                  <button
                    key={style.id}
                    type="button"
                    className={`${styles.styleCard} ${selected ? styles.styleCardActive : ''}`}
                    onClick={() => handleSelectStyle(activeNiche.id, style.id)}
                  >
                    <span className={styles.styleName}>{style.nome}</span>
                    <span className={styles.swatches} aria-hidden>
                      <span style={{ background: style.primaria }} title="Primária (60%)" />
                      <span style={{ background: style.secundaria }} title="Secundária (30%)" />
                      <span style={{ background: style.destaque }} title="Destaque (10%)" />
                    </span>
                    <span className={styles.styleVibe}>{style.vibe}</span>
                    <span className={styles.styleLegend}>
                      <span>60% {style.primaria}</span>
                      <span>30% {style.secundaria}</span>
                      <span>10% {style.destaque}</span>
                    </span>
                  </button>
                )
              })}
            </div>

            <div className={styles.customSection}>
              <button
                type="button"
                className={`${styles.styleCard} ${styles.customCard} ${isCustom ? styles.styleCardActive : ''}`}
                onClick={handleSelectCustom}
              >
                <span className={styles.styleName}>Personalizado</span>
                <span className={styles.swatches} aria-hidden>
                  <span style={{ background: customColors.primaria }} title="Primária (60%)" />
                  <span style={{ background: customColors.secundaria }} title="Secundária (30%)" />
                  <span style={{ background: customColors.destaque }} title="Destaque (10%)" />
                </span>
                <span className={styles.styleVibe}>Defina as três cores ao seu gosto — 60%, 30% e 10%.</span>
                <span className={styles.styleLegend}>
                  <span>60% {customColors.primaria}</span>
                  <span>30% {customColors.secundaria}</span>
                  <span>10% {customColors.destaque}</span>
                </span>
              </button>

              {isCustom && (
                <div className={styles.customPickers}>
                  {PALETTE_SLOTS.map(({ key, pct, title }) => (
                    <label key={key} className={styles.colorField}>
                      <span className={styles.colorFieldLabel}>
                        <span className={styles.colorPct}>{pct}</span>
                        <span className={styles.colorFieldHint}>{title}</span>
                      </span>
                      <span className={styles.colorInputs}>
                        <input
                          type="color"
                          className={styles.colorSwatchInput}
                          value={customColors[key]}
                          onChange={(e) => handleCustomColorChange(key, e.target.value)}
                          aria-label={`Cor ${pct}`}
                        />
                        <input
                          type="text"
                          className={styles.colorHexInput}
                          value={customColors[key]}
                          onChange={(e) => handleCustomColorChange(key, e.target.value)}
                          spellCheck={false}
                          maxLength={7}
                          aria-label={`Código hexadecimal ${pct}`}
                        />
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </section>

          <form className={styles.actions} onSubmit={handleSave}>
            <button type="submit" className={adminStyles.btnSalvar} disabled={!canSave}>
              {saving ? 'A guardar…' : 'Guardar tema'}
            </button>
            <button
              type="button"
              className={adminStyles.btnCancel}
              onClick={handleReset}
              disabled={saving}
            >
              Restaurar padrão
            </button>
          </form>
        </div>

        <aside className={styles.previewCol}>
          <section className={styles.previewCard}>
            <h2 className={styles.cardTitle}>Pré-visualização</h2>
            <p className={styles.cardHint}>
              {activeNiche.nome} · {activeStyle.nome}
            </p>
            <div
              key={previewKey}
              className={styles.previewShell}
              style={themeToScopeStyle(previewTheme)}
            >
              <div className={styles.previewHeader}>
                <span>Menu da app</span>
                <span className={styles.previewDot} />
              </div>
              <div className={styles.previewBody}>
                <div className={styles.previewCardInner}>
                  <p className={styles.previewMuted}>Cartão de conteúdo</p>
                  <strong>Agendar serviço</strong>
                </div>
                <div className={styles.previewMenuRow}>
                  <span className={styles.previewMenuItem}>Serviços</span>
                  <span className={`${styles.previewMenuItem} ${styles.previewMenuItemActive}`}>
                    Agendar
                  </span>
                  <span className={styles.previewMenuItem}>Perfil</span>
                </div>
                <button type="button" className={styles.previewCta}>
                  Marcar horário
                </button>
              </div>
            </div>
            {savedEntry && (
              <p className={styles.savedHint}>
                Tema activo: <strong>{savedEntry.niche.nome}</strong> — {savedEntry.style.nome}
              </p>
            )}
            {!savedEntry && savedCustom && savedNicheName && (
              <p className={styles.savedHint}>
                Tema activo: <strong>{savedNicheName}</strong> — Personalizado
              </p>
            )}
          </section>
        </aside>
      </div>
    </div>
  )
}
